/**
 * `React.lazy` that can be warmed up ahead of time. Once `preload()` resolved, the component renders the
 * loaded module directly: a freshly mounted lazy element otherwise suspends for one tick even when its
 * chunk is cached, and that tick paints the Suspense fallback (an empty panel) in the middle of a transition.
 */
import { createElement, lazy, type ComponentType } from 'react';

export type PreloadableComponent<P extends object> = ComponentType<P> & { preload(): Promise<void> };

export function lazyWithPreload<P extends object>(factory: () => Promise<{ default: ComponentType<P> }>): PreloadableComponent<P> {
  let loaded: ComponentType<P> | undefined;
  let pending: Promise<void> | undefined;
  const preload = () => {
    pending ??= factory().then((m) => { loaded = m.default; }, (e: unknown) => { pending = undefined; throw e; });
    return pending;
  };
  const Lazy = lazy(() => preload().then(() => ({ default: loaded! })));
  const Component = (props: P) => createElement(loaded ?? Lazy, props);
  return Object.assign(Component, { preload });
}
